
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useEffect, useMemo } from "react";
import { useWallet } from "../../hooks/useWallet";
import { getWalletTransactions } from "../../services/transactionService";
import { Transaction, ViewState } from "../../types";
import { Loader2, ArrowLeft, ExternalLink, CheckCircle, Clock, AlertCircle, Search, Filter, Download, Lock } from "lucide-react";
import { Card } from "../ui/Card";
import { Button } from "../ui/Button";
import { TokenIcon, ChainIcon } from "../ui/ChainIcons";

interface TransactionHistoryProps {
    onNavigate: (view: ViewState) => void;
}

const PAGE_SIZE = 12;

export const TransactionHistoryView: React.FC<TransactionHistoryProps> = ({ onNavigate }) => {
    const { walletAddress, isConnected, balances } = useWallet();
    const [transactions, setTransactions] = useState<Transaction[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalTransactions, setTotalTransactions] = useState(0);

    const totalPages = Math.ceil(totalTransactions / PAGE_SIZE);
    
    const tokenMap = useMemo(() => new Map(balances.map(token => [token.type, token])), [balances]);

    useEffect(() => {
        const fetchAllTransactions = async () => {
            if (isConnected && walletAddress) {
                setIsLoading(true);
                try {
                    const { transactions: fetched, total } = await getWalletTransactions(walletAddress, currentPage, PAGE_SIZE);
                    const txsWithData = fetched.map(tx => {
                        const token = tokenMap.get(tx.asset);
                        return {
                            ...tx,
                            usdAmount: token ? tx.amount * token.priceUsd : undefined
                        };
                    });
                    setTransactions(txsWithData);
                    setTotalTransactions(total);
                } catch (error) {
                    console.error("Failed to fetch transactions:", error);
                } finally {
                    setIsLoading(false);
                }
            }
        };
        fetchAllTransactions();
    }, [isConnected, currentPage, walletAddress, tokenMap]);

    const getStatusBadge = (status: Transaction['status']) => {
        switch(status) {
            case 'completed': return 'bg-emerald-500/5 text-emerald-500 border-emerald-500/10';
            case 'pending': return 'bg-amber-500/5 text-amber-500 border-amber-500/10 animate-pulse';
            case 'failed': return 'bg-red-500/5 text-red-500 border-red-500/10';
            default: return '';
        }
    };
    
    const isOutflow = (type: Transaction['type']) => ['payment', 'withdrawal', 'swap', 'lock'].includes(type);

    return (
        <div className="space-y-8 animate-fade-in max-w-[1400px] mx-auto pb-20 px-4 sm:px-0">
            <div className="flex flex-col md:flex-row justify-between items-center md:items-end gap-6">
                <div className="flex items-center gap-5 w-full md:w-auto">
                    <Button variant="outline" size="sm" onClick={() => onNavigate('wallet')} className="h-12 w-12 !p-0 rounded-sm border-stone-200 dark:border-white/10 shrink-0">
                        <ArrowLeft size={18} />
                    </Button>
                    <div className="text-left">
                        <h2 className="font-serif text-3xl font-bold text-[#0A0B0F] dark:text-white leading-tight">Transaction History</h2>
                        <p className="text-stone-500 text-sm mt-1 uppercase font-black tracking-widest opacity-60">All past transactions</p>
                    </div>
                </div>
                <div className="flex gap-2 w-full md:w-auto">
                    <Button variant="outline" size="sm" className="flex-1 md:flex-none h-12 rounded-sm text-[10px] font-black uppercase tracking-widest" icon={<Filter size={14}/>}>Filter</Button>
                    <Button variant="secondary" size="sm" className="flex-1 md:flex-none h-12 rounded-sm text-[10px] font-black uppercase tracking-widest shadow-lg" icon={<Download size={14}/>}>Export CSV</Button>
                </div>
            </div>

            <Card noPadding className="overflow-hidden border-stone-200 dark:border-[#2D3142] shadow-xl rounded-sm">
                <div className="p-4 border-b border-stone-100 dark:border-white/5 bg-gray-50/50 dark:bg-white/[0.01] flex items-center gap-4">
                    <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" size={16} />
                        <input 
                            type="text" 
                            placeholder="Search by transaction hash or ID..." 
                            className="w-full pl-10 pr-4 py-2.5 bg-white dark:bg-black/20 border border-stone-200 dark:border-white/10 rounded-sm text-xs font-bold outline-none focus:border-nobel-gold transition-all"
                        />
                    </div>
                </div>
                <div className="overflow-x-auto custom-scrollbar">
                    <table className="w-full text-left border-collapse min-w-[900px]">
                        <thead>
                            <tr className="bg-stone-50 dark:bg-white/[0.02] text-stone-500 text-[10px] font-black uppercase tracking-[0.25em] border-b border-stone-200 dark:border-white/10">
                                <th className="p-6">Transaction ID</th>
                                <th className="p-6">Type</th>
                                <th className="p-6 text-right">Value (Asset / USD)</th>
                                <th className="p-6">Network</th>
                                <th className="p-6">Status</th>
                                <th className="p-6 text-right">Date</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-stone-50 dark:divide-white/[0.03]">
                            {isLoading ? (
                                <tr>
                                    <td colSpan={6} className="text-center p-20">
                                        <div className="flex flex-col items-center gap-4">
                                            <Loader2 size={32} className="animate-spin text-nobel-gold" />
                                            <span className="text-[10px] font-black uppercase tracking-[0.3em] text-stone-400">Loading transactions...</span>
                                        </div>
                                    </td>
                                </tr>
                            ) : transactions.length > 0 ? transactions.map(tx => (
                                <tr key={tx.id} className="hover:bg-stone-50/50 dark:hover:bg-white/[0.01] transition-colors group cursor-default">
                                    <td className="p-6">
                                        <div className="flex items-center gap-4">
                                            <div className="w-10 h-10 rounded-sm bg-stone-100 dark:bg-white/5 flex items-center justify-center border border-stone-200 dark:border-white/10 shadow-inner group-hover:scale-105 transition-transform">
                                                <TokenIcon symbol={tx.asset} className="w-6 h-6" />
                                            </div>
                                            <div className="min-w-0">
                                                <div className="font-bold text-sm text-[#0A0B0F] dark:text-white truncate max-w-[150px]">{tx.id}</div>
                                                <div className="font-mono text-[10px] text-stone-400 mt-1 flex items-center gap-1.5">
                                                    {tx.txHash.slice(0, 16)}...
                                                    <a href={`${tx.explorerUrl}${tx.txHash}`} target="_blank" rel="noopener noreferrer" className="text-stone-300 hover:text-nobel-gold transition-colors">
                                                        <ExternalLink size={10} />
                                                    </a>
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="p-6">
                                        <span className="text-xs font-black uppercase tracking-widest text-stone-600 dark:text-stone-400">{tx.type}</span>
                                    </td>
                                    <td className="p-6 text-right">
                                        <div className={`font-mono text-sm font-bold ${isOutflow(tx.type) ? 'text-stone-900 dark:text-white' : 'text-emerald-500'}`}>
                                            {isOutflow(tx.type) ? '-' : '+'}{tx.amount.toLocaleString(undefined, { maximumFractionDigits: 4 })} {tx.asset}
                                        </div>
                                        {tx.usdAmount !== undefined && <div className="text-[10px] font-black text-stone-400 tracking-widest mt-1">${tx.usdAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} <span className="font-sans opacity-50 text-[8px]">USD</span></div>}
                                    </td>
                                    <td className="p-6">
                                        <div className="flex items-center gap-2">
                                            <ChainIcon chain={tx.chain} className="w-4 h-4" />
                                            <span className="text-[10px] font-black uppercase tracking-widest text-stone-500">{tx.chain} Mainnet</span>
                                        </div>
                                    </td>
                                    <td className="p-6">
                                        <span className={`px-2.5 py-1 rounded-sm text-[9px] font-black uppercase tracking-widest border ${getStatusBadge(tx.status)}`}>
                                            {tx.status}
                                        </span>
                                    </td>
                                    <td className="p-6 text-right text-[11px] text-stone-500 font-mono font-bold uppercase">
                                        {new Date(tx.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                                        <div className="text-[9px] opacity-40">{new Date(tx.date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
                                    </td>
                                </tr>
                            )) : (
                                <tr>
                                    <td colSpan={6} className="text-center p-20 opacity-40">
                                        <Clock size={48} className="mx-auto mb-4" />
                                        <p className="text-sm font-black uppercase tracking-widest">No Transaction History Found</p>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
                {/* Pagination Controls */}
                <div className="p-6 border-t border-stone-200 dark:border-white/10 flex flex-col sm:flex-row justify-between items-center bg-gray-50/50 dark:bg-black/20 gap-4">
                    <span className="text-[10px] font-black uppercase tracking-widest text-stone-400">
                        Page {currentPage} of {totalPages || 1} • Total Transactions: {totalTransactions}
                    </span>
                    <div className="flex gap-2">
                        <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => { setCurrentPage(p => p - 1); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                            disabled={currentPage === 1}
                            className="h-10 rounded-sm text-[10px] font-black uppercase"
                        >
                            Previous
                        </Button>
                        <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => { setCurrentPage(p => p + 1); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                            disabled={currentPage === totalPages || totalPages === 0}
                            className="h-10 rounded-sm text-[10px] font-black uppercase"
                        >
                            Next
                        </Button>
                    </div>
                </div>
            </Card>

            <div className="flex justify-center pt-8 border-t border-stone-100 dark:border-white/5">
                <p className="text-[9px] text-stone-400 font-black uppercase tracking-[0.4em] opacity-40 flex items-center gap-2">
                    <Lock size={10} className="text-emerald-500" /> Secured by GhostPay
                </p>
            </div>
        </div>
    );
};
